// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { type Draft, enableMapSet, produce } from 'immer';
import { createDataProvider } from '../providers';

enableMapSet();

/**
 * Definition of a single step in a multi-step flow.
 *
 * @typeParam M - Type of the optional metadata object for this step.
 * @typeParam T - String literal type for the step id (e.g. `'intro' | 'form' |
 *   'done'`).
 */
export interface StepDefinition<M extends Record<string, any>, T extends string> {
  /** Unique identifier for the step. */
  id: T;
  /**
   * Optional metadata (e.g. form data) for the step; can be updated via
   * `upsertMeta`.
   */
  meta?: M;
  /** React element to render when this step is active. */
  element: ReactElement;
}

/** Array of step definitions. */
export type StepDefinitions<
  M extends Record<string, any>,
  T extends string,
> = Array<StepDefinition<M, T>>;

/**
 * Options to initialize the steps hook.
 *
 * @typeParam M - Type of step metadata.
 * @typeParam T - Step id type.
 */
export interface UseStepsOptions<M extends Record<string, any>, T extends string> {
  /** Ordered list of step definitions (id, meta, element). */
  definitions: StepDefinitions<M, T>;
  /** Id of the step to show initially. */
  defaultStep: T;
}

/**
 * State and actions returned by {@link useSteps}.
 *
 * @typeParam M - Type of step metadata.
 * @typeParam T - Step id type.
 */
export interface StepsState<M extends Record<string, any>, T extends string> {
  /** Id of the current step. */
  current: T;
  /** Zero-based index of the current step in the definitions array. */
  stepIndex: number;
  /** Map of step id to step definition (includes mutable meta). */
  steps: Map<T, StepDefinition<M, T>>;
  /** Total number of steps. */
  length: number;
  /** Whether the current step is the last one. */
  isLastStep: boolean;
  /** Whether the current step is the first one. */
  isFirstStep: boolean;
  /** Definition of the current step. */
  step?: StepDefinition<M, T>;
  /** React element of the current step (convenience for `step?.element`). */
  element?: ReactElement;
  /** Metadata of the current step (convenience for `step?.meta`). */
  meta?: M;
  /** Updates meta for a step by id (immer draft or replacement object). */
  upsertMeta: (id: T, draft: Draft<M> | ((draft: Draft<M>) => void)) => void;
  /** Advances to the next step; no-op if already on last step. */
  next: () => void;
  /** Goes back to the previous step; no-op if already on first step. */
  prev: () => void;
  /** Jumps to a step by id; throws if id is not in definitions. */
  navigate: (step: T) => void;
  /** Resets to defaultStep and reinitializes steps from definitions. */
  reset: () => void;
}

/**
 * Hook for a multi-step flow with optional per-step metadata.
 *
 * Keeps the current step index, renders the active step's element, and provides
 * `next`, `prev`, `navigate`, and `reset`. Step metadata can be updated with
 * `upsertMeta` (uses Immer for immutable updates).
 *
 * @example Basic wizard
 *
 * ```tsx
 * type StepId = 'intro' | 'form' | 'done';
 * const definitions: StepDefinitions<{ name: string }, StepId> = [
 *   { id: 'intro', element: <Intro /> },
 *   { id: 'form', meta: { name: '' }, element: <Form /> },
 *   { id: 'done', element: <Done /> },
 * ];
 *
 * const { current, element, next, prev, isFirstStep, isLastStep } = useSteps({
 *   definitions,
 *   defaultStep: 'intro',
 * });
 *
 * return (
 *   <>
 *     {element}
 *     {!isFirstStep && <Button onClick={prev}>Back</Button>}
 *     {!isLastStep && <Button onClick={next}>Next</Button>}
 *   </>
 * );
 * ```
 *
 * @example Updating step meta with upsertMeta
 *
 * ```tsx
 * steps.upsertMeta('form', draft => {
 *   draft.name = 'Alice';
 * });
 * ```
 *
 * @typeParam M - Type of step metadata (e.g. form values). Defaults to
 *   `Record<string, any>`.
 * @typeParam T - Step id type. Defaults to `string`.
 * @param options - `definitions` (ordered steps) and `defaultStep` (initial step
 *   id).
 * @returns {@link StepsState} : current step info, `element`, `meta`, and
 *   navigation/update functions.
 */
export const useSteps = <M extends Record<string, any>, T extends string = string>({
  defaultStep,
  definitions,
}: UseStepsOptions<M, T>): StepsState<M, T> => {
  const [current, setCurrent] = useState(defaultStep);

  // istanbul ignore next
  const [steps, setSteps] = useState(
    () =>
      new Map(
        definitions.map(value => [
          value.id,
          {
            ...value,
            meta: value.meta || ({} as M),
          },
        ]),
      ),
  );

  // istanbul ignore next
  const length = useMemo(() => steps.size, [steps]);

  // istanbul ignore next
  const stepIndex = useMemo(
    () => definitions.findIndex(value => value.id === current),
    [current, definitions],
  );

  // istanbul ignore next
  const isLastStep = useMemo(
    () => current === definitions[length - 1]?.id,
    [current, definitions, length],
  );

  // istanbul ignore next
  const isFirstStep = useMemo(
    () => current === definitions[0]?.id,
    [current, definitions],
  );

  // istanbul ignore next
  const step = useMemo(() => steps.get(current), [steps, current]);

  // istanbul ignore next
  const element = useMemo(() => step?.element, [step]);

  // istanbul ignore next
  const meta = useMemo(() => step?.meta, [step]);

  // istanbul ignore next
  const upsertMeta = useCallback<StepsState<M, T>['upsertMeta']>((id, value) => {
    setSteps(prev =>
      produce(prev, draft => {
        if (draft.has(id as Draft<T>)) {
          const step = draft.get(id as Draft<T>)!;

          const changes =
            typeof value === 'function'
              ? (value as (prevState: Draft<M>) => Draft<M>)(step.meta)
              : value;

          step.meta = changes;
        }
      }),
    );
  }, []);

  const goToNextStep = useCallback(() => {
    setCurrent(prevCurrent => {
      const index = definitions.findIndex(value => value.id === prevCurrent);

      // istanbul ignore if
      if (index === -1) {
        return prevCurrent;
      }

      if (index >= definitions.length - 1) {
        return prevCurrent;
      }

      return definitions[index + 1].id;
    });
  }, [definitions]);

  const goToPreviousStep = useCallback(() => {
    setCurrent(prevCurrent => {
      const index = definitions.findIndex(value => value.id === prevCurrent);

      // istanbul ignore if
      if (index === -1) {
        return prevCurrent;
      }

      if (index === 0) {
        return prevCurrent;
      }

      return definitions[index - 1].id;
    });
  }, [definitions]);

  const goToStep = useCallback(
    (step: T) => {
      const index = definitions.findIndex(value => value.id === step);

      if (index < 0) {
        throw new Error(
          `The step ${step} does not exist. Please check that the step is correctly defined.`,
        );
      }

      setCurrent(step);
    },
    [definitions],
  );

  // istanbul ignore next
  const reset = useCallback(() => {
    setCurrent(defaultStep);

    setSteps(
      new Map(
        definitions.map(value => [
          value.id,
          {
            ...value,
            meta: value.meta || ({} as M),
          },
        ]),
      ),
    );
  }, [defaultStep, definitions]);

  return {
    current,
    isFirstStep,
    isLastStep,
    length,
    steps,
    step,
    element,
    meta,
    upsertMeta,
    next: goToNextStep,
    prev: goToPreviousStep,
    navigate: goToStep,
    reset,
    stepIndex,
  };
};

/**
 * Creates a set of options for the steps hook.
 *
 * @typeParam M - Type of step metadata.
 * @typeParam T - Step id type.
 * @param definitions - The definitions of the steps.
 * @param defaultStep - The default step to start on.
 * @returns The options for the steps hook.
 */
export const createStepsOptions = <
  M extends Record<string, any>,
  T extends string = string,
>({
  definitions,
  defaultStep,
}: UseStepsOptions<M, T>): UseStepsOptions<M, T> => {
  const stepIds = definitions.map(definition => definition.id);

  if (!stepIds.includes(defaultStep))
    throw new RangeError(`defaultStep ${defaultStep} is not a valid step id`);

  return {
    definitions,
    defaultStep,
  };
};

/**
 * Context-based access to the steps state from {@link useSteps}. Use
 * {@link StepsProvider} to wrap the tree, then {@link useStepsData} in children.
 */
export const { useStepsData, StepsProvider, StepsDataContext } = createDataProvider(
  {
    factory: useSteps,
    name: 'Steps',
  },
);
